import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Users, LogOut, Download } from "lucide-react";
import AdminLogin from "@/components/AdminLogin";
import { isAuthenticated, clearSession, attachAuthInterceptors } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTY_TYPE_LABELS = {
  grunt_rolny: "Działka rolna",
  dach: "Dach hali / budynku",
  dzialka_inna: "Inna działka",
  inne: "Nie wiem / inne",
};

const PROPERTY_TYPE_COLORS = {
  grunt_rolny: "bg-emerald-100 text-emerald-700",
  dach: "bg-amber-100 text-amber-700",
  dzialka_inna: "bg-slate-200 text-slate-700",
  inne: "bg-slate-100 text-slate-500",
};

export default function AdminLeads() {
  const [authenticated, setAuthenticated] = useState(() => isAuthenticated());
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    attachAuthInterceptors(() => setAuthenticated(false));
  }, []);

  useEffect(() => {
    if (!authenticated) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    axios
      .get(`${API}/leads`)
      .then((r) => {
        setLeads(r.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err?.response?.status !== 401) {
          setError("Nie udało się pobrać zgłoszeń. Spróbuj odświeżyć stronę.");
        }
      });
  }, [authenticated]);

  const handleLogout = () => {
    clearSession();
    setAuthenticated(false);
  };

  const exportCSV = () => {
    const rows = [
      ["Lp.", "Typ nieruchomości", "Imię i nazwisko", "Telefon", "E-mail", "Adres działki", "Data zgłoszenia", "Zgoda RODO"],
      ...leads.map((l, i) => [
        i + 1,
        PROPERTY_TYPE_LABELS[l.property_type] || l.property_type || "—",
        l.name,
        l.phone,
        l.email || "—",
        l.address,
        new Date(l.created_at).toLocaleString("pl-PL"),
        l.rodo_consent ? "Tak" : "Brak",
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `telecom-bts-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authenticated) {
    return <AdminLogin onSuccess={() => setAuthenticated(true)} />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors text-sm"
              data-testid="back-to-home"
            >
              <ArrowLeft className="w-4 h-4" />
              Powrót na stronę
            </Link>
            <div className="h-4 w-px bg-slate-200" />
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-600" />
              <span className="font-display font-semibold text-slate-800">
                Panel Administratora
              </span>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-slate-500 hover:text-red-500 transition-colors text-sm"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-4 h-4" />
            Wyloguj
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight">
              Zgłoszenia
            </h1>
            <p className="text-slate-500 mt-1">Leady z formularza kontaktowego</p>
          </div>
          {leads.length > 0 && (
            <button
              onClick={exportCSV}
              className="flex items-center gap-2 bg-white border border-slate-200 hover:border-emerald-400 text-slate-700 hover:text-emerald-700 text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"
              data-testid="export-csv-button"
            >
              <Download className="w-4 h-4" />
              Eksportuj CSV
            </button>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
            <span className="font-semibold text-slate-700 text-sm">
              Wszystkie zgłoszenia{" "}
              <span
                className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded-full ml-1"
                data-testid="leads-count"
              >
                {leads.length}
              </span>
            </span>
          </div>

          {loading ? (
            <div className="p-16 text-center text-slate-500" data-testid="leads-loading">
              <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              Ładowanie zgłoszeń...
            </div>
          ) : error ? (
            <div className="p-16 text-center text-red-600" role="alert">
              {error}
            </div>
          ) : leads.length === 0 ? (
            <div className="p-16 text-center text-slate-500" data-testid="leads-empty">
              Brak zgłoszeń. Formularz jest aktywny i gotowy do przyjmowania kontaktów.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="leads-table">
                <thead>
                  <tr className="bg-slate-50 text-left">
                    {["Lp.", "Typ", "Imię i nazwisko", "Telefon", "E-mail", "Adres działki", "RODO", "Data zgłoszenia"].map(
                      (h) => (
                        <th
                          key={h}
                          className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {leads.map((lead, i) => {
                    const typeLabel = PROPERTY_TYPE_LABELS[lead.property_type] || "—";
                    const typeColor = PROPERTY_TYPE_COLORS[lead.property_type] || "bg-slate-100 text-slate-500";
                    return (
                      <tr
                        key={lead.id}
                        className="hover:bg-slate-50 transition-colors"
                        data-testid={`lead-row-${i}`}
                      >
                        <td className="px-6 py-4 text-sm text-slate-500">{i + 1}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${typeColor}`}>
                            {typeLabel}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-slate-900">
                          {lead.name}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">{lead.phone}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {lead.email ? (
                            <a href={`mailto:${lead.email}`} className="hover:text-emerald-700 hover:underline">
                              {lead.email}
                            </a>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 max-w-xs">{lead.address}</td>
                        <td className="px-6 py-4">
                          <span
                            className={`text-xs font-semibold px-2 py-1 rounded-full ${
                              lead.rodo_consent
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-slate-100 text-slate-500"
                            }`}
                          >
                            {lead.rodo_consent ? "Tak" : "Brak"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500 whitespace-nowrap">
                          {new Date(lead.created_at).toLocaleString("pl-PL")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
